import { gql, useQuery } from '@apollo/client'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useLanguage } from '@/context/LanguageContext'
import { ContentBlockStyle, ContentBlockType, ContentBlockVideoType } from './types'

// Extracted verbatim from ContentBlockForm.tsx and ContentBlockFormBody.tsx,
// which had duplicated copies of these items. `PageSelector` and `imageTypes`
// differed only in Prettier line-wrapping (and a trailing comma) between the
// two copies — not a behavioral fork — so the wrapped (ContentBlockForm.tsx)
// formatting was kept as canonical since it's the one that actually fits this
// repo's printWidth: 80 .prettierrc config; the single-line versions in
// ContentBlockFormBody.tsx exceeded it.

export const GET_PAGES_FOR_SELECTOR = gql`
  query GET_PAGES_FOR_SELECTOR {
    findManyPage(take: 100, orderBy: [{ createdAt: desc }]) {
      id
      code
      hero_image {
        src
      }
      page_profiles {
        title
        language
      }
    }
  }
`

export function PageSelector({
  value,
  onChange,
}: {
  value: string
  onChange: (pageId: string) => void
}) {
  const { data, loading } = useQuery(GET_PAGES_FOR_SELECTOR)
  const { languageId, allLanguages } = useLanguage()

  const pages = data?.findManyPage ?? []

  const getPageTitle = (page: {
    code: string
    page_profiles: { title: string; language: string }[]
  }) => {
    const currentLang = allLanguages[languageId] || allLanguages[0]
    const profile = page.page_profiles?.find(
      (p: { language: string }) => p.language === currentLang
    )
    return profile?.title || page.code
  }

  return (
    <Select
      onValueChange={onChange}
      value={value === 'select' ? undefined : value}
    >
      <SelectTrigger>
        <SelectValue
          placeholder={loading ? 'Loading pages...' : 'Select a page'}
        />
      </SelectTrigger>
      <SelectContent>
        {pages.map(
          (page: {
            id: string
            code: string
            page_profiles: { title: string; language: string }[]
          }) => (
            <SelectItem key={page.id} value={page.id}>
              {getPageTitle(page)}
            </SelectItem>
          )
        )}
      </SelectContent>
    </Select>
  )
}

export const imageTypes: ContentBlockType[] = [
  'only_image',
  'text_and_image',
  'gallery',
  'email_signup',
]
export const videoTypes: ContentBlockType[] = ['only_video', 'text_and_video']

export const videoTypeOptions: { value: ContentBlockVideoType; label: string }[] = [
  { value: 'youtube', label: 'YouTube' },
  { value: 'vimeo', label: 'Vimeo' },
  { value: 'uploaded', label: 'Uploaded' },
]

export const textStyles: { value: ContentBlockStyle; label: string }[] = [
  { value: '1_column_title_top', label: '1 Column Title Top' },
  { value: '2_columns_title_top_center', label: '2 Columns Title Top Center' },
  { value: '2_columns_1_3', label: '2 Columns 1/3 + 2/3' },
  { value: '3_columns_title_left', label: '3 Columns Title Left' },
]

export const galleryStyles: { value: ContentBlockStyle; label: string }[] = [
  { value: 'masonry', label: 'Masonry' },
  { value: 'spread_harmony', label: 'Spread Harmony' },
  { value: 'scroll_rhythm', label: 'Scroll Rhythm' },
  { value: 'gallery_side_by_side', label: 'Side by Side' },
]

export const textAndImageStyles: { value: ContentBlockStyle; label: string }[] = [
  { value: 'side_by_side', label: 'Side by Side' },
  { value: 'text_on_image', label: 'Text on Image' },
  { value: 'image_left', label: 'Image Left' },
]

export const buttonStyles: { value: ContentBlockStyle; label: string }[] = [
  { value: 'outline', label: 'Outline' },
  { value: 'filled', label: 'Filled' },
]

export const contentBlockTypes: { value: ContentBlockType; label: string }[] = [
  { value: 'only_text', label: 'Text Only' },
  { value: 'only_image', label: 'Image Only' },
  { value: 'text_and_image', label: 'Text & Image' },
  { value: 'only_video', label: 'Video Only' },
  { value: 'text_and_video', label: 'Text & Video' },
  { value: 'gallery', label: 'Gallery' },
  { value: 'button', label: 'Button' },
  { value: 'photo_links', label: 'Photo Links' },
  { value: 'category_tiles', label: 'Category Tiles' },
  { value: 'product_grid', label: 'Product Grid' },
  { value: 'page_grid', label: 'Page Grid' },
  { value: 'email_signup', label: 'Email Signup' },
  { value: 'pdf_flipbook', label: 'PDF Flipbook' },
]
